import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { OrderData } from '@lfw/roesberg.liveforms.core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { catchError, filter, firstValueFrom, map, switchMap, tap, throwError } from 'rxjs';
import { LfwRouterService } from '../../../route/lfw-router.service';
import { AppToolbarButtonComponent } from '../../../shared/components/app-toolbar-button/app-toolbar-button.component';
import { AppToolbarTextComponent } from '../../../shared/components/app-toolbar-text/app-toolbar-text.component';
import { AppToolbarComponent } from '../../../shared/components/app-toolbar/app-toolbar.component';
import { ErrorDialogComponent } from '../../../shared/dialogs/error-dialog/error-dialog.component';
import { InfoService } from '../../../shared/services/info.service';
import { OrdersService } from '../../orders.service';

@Component({
  selector: 'lfw-online-order-report-view',
  imports: [
    AppToolbarButtonComponent,
    AppToolbarComponent,
    AppToolbarTextComponent,
    TranslatePipe,
    NgxExtendedPdfViewerModule
  ],
  templateUrl: './online-order-report-view.component.html',
  styleUrl: './online-order-report-view.component.css'
})
export class OnlineOrderReportViewComponent {
  order: OrderData | undefined = undefined;
  blob: Blob | undefined;

  private readonly routerService = inject(LfwRouterService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly infoService = inject(InfoService);
  private readonly ordersService = inject(OrdersService);
  private readonly dialog = inject(MatDialog);

  constructor() {
    this.infoService.clearStatus();

    this.route.paramMap
      .pipe(
        takeUntilDestroyed(),
        map((params) => params.get('order') || ''),
        filter((orderId: string) => !!orderId),
        switchMap((orderId: string) => this.ordersService.getOnlineOrder(orderId)),
        tap((order: OrderData) => (this.order = order)),
        switchMap((order: OrderData) => this.ordersService.getReport(order.id)),
        tap((blob) => (this.blob = blob)),
        catchError(async (err) => {
          const parsedError = JSON.parse(await err.error.text());
          firstValueFrom(
            this.dialog
              .open(ErrorDialogComponent, {
                data: {
                  title: this.translate.instant('DIALOGS.ERROR'),
                  message: parsedError?.detail || 'ORDERS.LOADING_REPORT_ERROR'
                }
              })
              .afterClosed()
              .pipe(tap(() => this.routerService.navigateToOrdersRoot()))
          );
          return throwError(() => err);
        })
      )
      .subscribe();
  }

  get caption(): string {
    return this.order?.text ?? '';
  }

  error(formId: string) {
    this.infoService.setStatus(this.translate.instant('FORM.DLG_FORM_ID_NOT_FOUND', { id: formId }));
  }

  close(event: any) {
    this.routerService.navigateBack();
  }
}
